/**
 * SubmitChangeset (§7.2): changes are folded in order over a scratch state; each is authorized, policy-checked and
 * validated. All allowed → applied as one changeset. Some propose → one plan_change proposal (propose_all), or the
 * dependency-closed allowed subset applied now and the rest proposed (apply_allowed, the default).
 */
import { collapseAsked } from "../asked"
import { isMayor } from "../authorize"
import { autonomyOf, levelOf } from "../autonomy"
import { cardPersonOf, chainApprover } from "../card-chain"
import { planSubjectKey, sameSubjectCard } from "../card-subject"
import { deriveId } from "../hash"
import { routeCard } from "../multiplayer"
import { inverseOf, revertTo } from "../plan"
import { evaluatePolicy, exemptStamp, type PolicyVerdict, policyGrants } from "../policy"
import { openProposal } from "../runs"
import { agentOf, checksByPerson, edgeOf, isClosed, itemOf, leadOf, must } from "../state"
import type {
  Actor,
  Approver,
  AskedReason,
  ChangesetId,
  EntityRef,
  GraphCommand,
  Ids,
  Item,
  PlanQuestion,
  PolicyStamp,
  ProjectEvent,
  ProjectState,
  Proposal,
} from "../types"
import { applyGraph, changedAgentOf, crossOwnerTarget, dependencies } from "./graph"
import { actorTaint, forbidden, invalid, Rejected, reject, Tx } from "./tx"

export interface ChangesetInput {
  readonly changesetId: ChangesetId
  readonly rationale: string
  readonly changes: ReadonlyArray<GraphCommand>
  readonly onPropose: "apply_allowed" | "propose_all"
  readonly revertOf?: number | null
  readonly undoOf?: ChangesetId | null
  /** Wave O1 (decision 0061, decide first): kept on the plan_change card if any part becomes one. */
  readonly questions?: ReadonlyArray<PlanQuestion>
  readonly containerId?: Ids["ItemId"]
  /** Decision 0134: a person's import of history (marks ChangesetApplied; its events wake no agent). */
  readonly import?: true
}

/** Worker graph rights: one CreateItem under its own item (decide checks the parent). No tool offers it this release. */
const authorizeGraph = (actor: Actor, changes: ReadonlyArray<GraphCommand>) => {
  if (actor._tag !== "worker") return
  if (changes.length !== 1 || changes[0]?._tag !== "CreateItem")
    forbidden("actor_kind", "a worker may only create one child of its own item")
}

/** The person whose item it is (G7): its person assignee, else its person owner. */
const personOfItem = (t: Item): Ids["UserId"] | null =>
  t.assignee?._tag === "user" ? t.assignee.userId : t.owner?._tag === "user" ? t.owner.userId : null

/**
 * G7 (decision 0035): an agent never silently undoes a person's choice. These changes to a person's item (its owner or
 * assignee is a person) become a proposal to that person whatever the policy says: reassigning it, closing it, moving
 * it, re-dating it or moving it to another area, and removing a `blocks` edge out of it.
 */
export const personItemOf = (
  s: ProjectState,
  c: GraphCommand,
): { readonly item: Item; readonly userId: Ids["UserId"] } | undefined => {
  const of = (id: Item["id"]) => {
    const t = itemOf(s, id)
    const userId = t ? personOfItem(t) : null
    return t && userId ? { item: t, userId } : undefined
  }
  switch (c._tag) {
    case "AssignItem":
    case "CloseItem":
      return of(c.itemId)
    case "MoveItem": {
      // decision 0152: re-filing a closed item (a new parent only) undoes no choice of the person: what they did,
      // its status and evidence stay. A restructure stays one plan, not a card per person.
      const t = itemOf(s, c.itemId)
      if (t && isClosed(t) && c.status === undefined) return undefined
      return of(c.itemId)
    }
    case "UpdateItem":
      return c.patch.dueAt !== undefined || c.patch.notBefore !== undefined || c.patch.areaId !== undefined
        ? of(c.itemId)
        : undefined
    case "RemoveEdge": {
      const e = edgeOf(s, c.edgeId)
      return e?.kind === "blocks" ? of(e.from.id) : undefined
    }
    default:
      return undefined
  }
}

/** A description a person last wrote changes only through a proposal to that person (decision 0035, `person_spec`). */
export const personSpecOf = (
  s: ProjectState,
  c: GraphCommand,
): { readonly item: Item; readonly userId: Ids["UserId"] } | undefined => {
  if (c._tag !== "UpdateItem" || c.patch.body === undefined) return undefined
  const t = itemOf(s, c.itemId)
  if (t?.specRev.by._tag !== "user" || c.patch.body === t.body) return undefined
  return { item: t, userId: t.specRev.by.userId }
}

/**
 * Wave A §4.5: at Act, an agent's edit to a description its owner wrote, on an agent-owned item, applies (with a diff
 * in its message and Undo). Only when the agent's CONFIGURED level is Act (not `autonomyOf`, which is null in every
 * person-started turn), the description's author is the agent's owner, and the item is not a person's.
 */
const ownerSpecAtAct = (
  s: ProjectState,
  actor: Extract<Actor, { _tag: "agent" }>,
  spec: { readonly item: Item; readonly userId: Ids["UserId"] },
): boolean => {
  const agent = agentOf(s, actor.agentId)
  return (
    agent !== undefined &&
    levelOf(agent) === "act" &&
    spec.userId === actor.onBehalfOf &&
    personOfItem(spec.item) === null
  )
}

/**
 * The person a change must go to whatever the policy says (K3 cross_owner, G7 person_item, person_spec, the brief
 * (decision 0049), and the autonomy level of a turn that changes started (decision 0048)). Wave A §4.4: at Act a new
 * release, bet or workstream and its move into Now apply (the agent tells the person, who can undo it).
 *
 * Decision 0126 §3 (0127): only what is someone's own names its person: another person's agent's settings (it thinks on
 * its keeper's machine and logins), a person's item, a description a person wrote. Everything else a person must
 * decide (an agent's mandate, the brief, Ask first) has `userId: null`: it is routed (`routeCard`), not sent to the
 * agent's keeper.
 */
const forcedApproverOf = (
  s: ProjectState,
  actor: Actor,
  c: GraphCommand,
):
  | {
      readonly userId: Ids["UserId"] | null
      readonly rule: string
      readonly why: string
      /** Wave B2 (decision 0057): the same rule as a typed reason for the card. */
      readonly asked: AskedReason
    }
  | undefined => {
  const target = crossOwnerTarget(s, actor, c)
  if (target)
    return {
      // decision 0126 §2: an agent's mandate is every member's to edit; its settings stay its keeper's
      userId: c._tag === "ReviseAgentCharter" ? null : target.ownerId,
      rule: "cross_owner",
      why:
        c._tag === "ReviseAgentCharter"
          ? `@${target.handle} is another person's agent: a person approves changes to its instructions`
          : `@${target.handle} belongs to another person, who approves changes to it`,
      asked: { rule: "cross_owner", subject: { _tag: "agent", id: target.id }, userId: target.ownerId },
    }
  if (actor._tag !== "agent") return undefined
  // an agent's level is set by a person: always a card, even when the policy allows `update_agent` (decision 0048 §3)
  if (c._tag === "UpdateAgent" && c.patch.autonomy !== undefined)
    return {
      userId: changedAgentOf(s, c)?.ownerId ?? actor.onBehalfOf,
      rule: "autonomy",
      why: "an agent's level is set by a person",
      asked: { rule: "agent_level" },
    }
  const person = personItemOf(s, c)
  if (person)
    return {
      userId: person.userId,
      rule: "person_item",
      why: `${person.item.ref} is a person's item: they approve changes to it`,
      asked: { rule: "person_item", subject: { _tag: "item", id: person.item.id }, userId: person.userId },
    }
  const spec = personSpecOf(s, c)
  if (spec && !ownerSpecAtAct(s, actor, spec))
    return {
      userId: spec.userId,
      rule: "person_spec",
      why: `a person wrote ${spec.item.ref}'s description: they approve changes to it`,
      asked: { rule: "person_spec", subject: { _tag: "item", id: spec.item.id }, userId: spec.userId },
    }
  // decision 0138: the brief is a rule a policy can grant (`allow revise_brief`); unmentioned it stays a card
  if (c._tag === "ReviseBrief" && !policyGrants(s, actor, c))
    return {
      userId: null,
      rule: "brief",
      why: "a person approves every change to the brief",
      asked: { rule: "brief" },
    }
  const level = autonomyOf(s, actor)
  if (level === "ask")
    return {
      userId: null,
      rule: "autonomy",
      why: "Ask first: a turn changes started proposes every change",
      asked: { rule: "ask_first" },
    }
  return undefined
}

/** The verdict for one change, including the forced proposals (K3, G7, frozen checks: after frozenAt, agents propose). */
const verdictFor = (tx: Tx, change: GraphCommand): PolicyVerdict => {
  // wave E (decision 0063, gap 8): membership takes over a leaver's agents for the workspace owner (decide checks)
  if (tx.actor._tag === "system" && tx.actor.process === "membership" && change._tag === "TakeOverAgent")
    return { _tag: "exempt", stamp: exemptStamp(null) }
  const v = evaluatePolicy(tx.state, tx.actor, change)
  if (v._tag === "never") return v
  const base = v._tag === "allow" || v._tag === "propose" ? v.stamp : exemptStamp(null)
  const forced = forcedApproverOf(tx.state, tx.actor, change)
  if (forced)
    return {
      _tag: "propose",
      stamp: { ...base, verdict: "propose", rule: forced.rule },
      approver: { _tag: "human", userId: forced.userId ?? routedPerson(tx, [change]) },
      why: forced.why,
      asked: forced.asked,
    }
  if (change._tag === "ReviseChecks" && tx.actor._tag !== "user") {
    const t = itemOf(tx.state, change.itemId)
    // decision 0116: frozen checks are a person's only when a person wrote (or approved) them; checks an agent wrote
    // the agent's own policy and mandate decide (the lead narrows a full `test` check to the unit tests the person
    // already said are enough)
    // decision 0138: a policy may grant it (`allow revise_checks {frozen: true}`); unmentioned it stays a card
    if (
      t !== undefined &&
      t.checksFrozenAt !== null &&
      checksByPerson(t) &&
      !policyGrants(tx.state, tx.actor, change)
    ) {
      return {
        _tag: "propose",
        stamp: { ...base, verdict: "propose", rule: "frozen_checks" },
        approver: { _tag: "human", userId: routedPerson(tx, [change]) },
        why: "the checks are frozen: changing them needs a person",
        asked: { rule: "frozen_checks", subject: { _tag: "item", id: t.id } },
      }
    }
  }
  return v
}

/**
 * Decision 0102: why `tx.actor` (an agent deciding a card addressed to it) could not make these changes itself, or
 * null when it could: each change, folded in order, must be allowed by its own policy with no person-reserved rule.
 */
export const outsideAuthority = (tx: Tx, changes: ReadonlyArray<GraphCommand>): string | null => {
  const scratch = new Tx(tx.state, tx.ctx)
  scratch.scope = { ...tx.scope }
  for (const [index, change] of changes.entries()) {
    const v = verdictFor(scratch, change)
    if (v._tag === "never") return `${change._tag} (change ${index}): your policy never allows it (${v.rule})`
    if (v._tag === "propose") return `${change._tag} (change ${index}): ${v.why}`
    try {
      scratch.withScope({ policy: v.stamp }, () => applyGraph(scratch, change))
    } catch (e) {
      if (!(e instanceof Rejected)) throw e
      const r = e.rejection
      const why =
        r._tag === "Invalid"
          ? r.issues.map((i) => i.message).join("; ")
          : r._tag === "Forbidden"
            ? r.detail
            : r._tag === "Duplicate"
              ? r.reason
              : r._tag
      return `${change._tag} (change ${index}) does not apply now: ${why}`
    }
  }
  return null
}

/**
 * Why a person decides these changes, judged now for `tx.actor` (a revised plan card, decision 0057): each change's
 * forced or policy reason; a change the policy would allow is in the card because the agent chose to ask.
 */
export const askedOfChanges = (tx: Tx, changes: ReadonlyArray<GraphCommand>): ReadonlyArray<AskedReason> =>
  collapseAsked(
    changes.map((c): AskedReason => {
      const v = verdictFor(tx, c)
      return v._tag === "propose" ? v.asked : { rule: "asked" }
    }),
  )

/** Fold the given changes into `tx` (events + ChangesetApplied). Throws Rejected with the failing changeIndex. */
export const applyChanges = (
  tx: Tx,
  input: Pick<ChangesetInput, "changesetId" | "rationale" | "revertOf" | "undoOf" | "import">,
  changes: ReadonlyArray<{
    readonly change: GraphCommand
    readonly index: number
    readonly stamp: PolicyStamp
  }>,
  proposalId: Ids["ProposalId"] | null,
): void => {
  const firstSeq = tx.state.seq + 1
  tx.withScope({ changesetId: input.changesetId }, () => {
    for (const { change, index, stamp } of changes) {
      try {
        tx.withScope({ policy: stamp }, () => applyGraph(tx, change))
      } catch (e) {
        if (e instanceof Rejected && e.rejection._tag === "Invalid")
          reject({ _tag: "Invalid", issues: e.rejection.issues.map((i) => ({ ...i, changeIndex: index })) })
        throw e
      }
    }
    tx.emit(
      {
        _tag: "ChangesetApplied",
        changesetId: input.changesetId,
        planVersion: tx.state.planVersion + 1,
        rationale: input.rationale,
        firstSeq: firstSeq as Ids["Seq"],
        changeCount: changes.length,
        proposalId,
        revertOf: input.revertOf ?? null,
        undoOf: input.undoOf ?? null,
        // decision 0134: only a person's import is marked (an agent's plan never is)
        ...(input.import === true && tx.actor._tag === "user" ? { import: true as const } : {}),
      },
      { policy: null },
    )
  })
}

const oneLine = (s: string): string => {
  const line = s.split("\n").find((l) => l.trim().length > 0) ?? s
  return line.length > 120 ? `${line.slice(0, 117)}…` : line
}

/** The items a set of changes touches (an existing one, or the one a change creates). */
const itemsOfChanges = (changes: ReadonlyArray<GraphCommand>): ReadonlyArray<Ids["ItemId"]> => {
  const ids: Ids["ItemId"][] = []
  for (const c of changes) {
    const x = c as { readonly itemId?: unknown; readonly parentId?: unknown }
    if (typeof x.itemId === "string") ids.push(x.itemId as Ids["ItemId"])
    if (typeof x.parentId === "string") ids.push(x.parentId as Ids["ItemId"])
  }
  return [...new Set(ids)]
}

/**
 * Decision 0126 §6 (0127): the person a card about these changes is for: `to`, the person responsible for an item they
 * touch, the run's asker, the project's admins (`routeCard`). Never by default the agent's keeper.
 */
const routedPerson = (
  tx: Tx,
  changes: ReadonlyArray<GraphCommand>,
  to?: Ids["UserId"] | undefined,
): Ids["UserId"] => routeCard(tx.state, tx.actor, { to, items: itemsOfChanges(changes) }) ?? tx.owner()

/**
 * Approver of an agent's plan_change: the routed person (`routedPerson`); for the Mayor, the lead (who may adopt) with
 * the Mayor's owner.
 */
const planApprover = (
  tx: Tx,
  actor: Actor,
  changes: ReadonlyArray<GraphCommand>,
  to: Ids["UserId"] | undefined,
): { approver: Approver; ownerId: Ids["UserId"] } => {
  if (actor._tag === "agent" && isMayor(tx.state, actor)) {
    const lead = leadOf(tx.state)
    const owner = must(tx.state.mayor, "mayor").ownerId
    return lead
      ? { approver: { _tag: "lead", agentId: lead.id, escalateTo: owner }, ownerId: owner }
      : { approver: { _tag: "human", userId: owner }, ownerId: owner }
  }
  const owner = actor._tag === "user" ? tx.owner() : routedPerson(tx, changes, to)
  return { approver: { _tag: "human", userId: owner }, ownerId: owner }
}

/**
 * K3 and G7: when an agent's proposed changes touch what is someone's own (another person's agent's settings, a
 * person's item or description), that person approves the whole proposal (and owns it). Changes for two different
 * people go in separate changesets. Forced changes without a person of their own (decision 0127) are routed.
 */
const forcedApprover = (
  tx: Tx,
  changes: ReadonlyArray<GraphCommand>,
): { approver: Approver; ownerId: Ids["UserId"] } | null => {
  const owners = new Set<Ids["UserId"]>()
  for (const c of changes) {
    const f = forcedApproverOf(tx.state, tx.actor, c)
    if (f?.userId) owners.add(f.userId)
  }
  if (owners.size === 0) return null
  if (owners.size > 1)
    invalid(
      "these changes need approval from different people: propose them in separate changesets",
      "changes",
    )
  const ownerId = [...owners][0] as Ids["UserId"]
  return { approver: { _tag: "human", userId: ownerId }, ownerId }
}

/** Open the plan_change proposal for the changes that need approval. */
export const proposePlanChange = (
  tx: Tx,
  input: {
    changesetId: ChangesetId
    rationale: string
    revertOf: number | null
    undoOf: ChangesetId | null
    /** Wave O1 (decision 0061): on the card only when given (stored bodies keep their hash). */
    questions?: ReadonlyArray<PlanQuestion> | undefined
    containerId?: Ids["ItemId"] | undefined
    /** Decision 0127: the person the agent names (`propose_plan{to}`), a working member. */
    to?: Ids["UserId"] | undefined
  },
  changes: ReadonlyArray<GraphCommand>,
  proposalId: Ids["ProposalId"],
  extra: Partial<
    Pick<
      Proposal,
      "reasoning" | "oneLine" | "risk" | "reversibility" | "blocks" | "decideBy" | "intentKey" | "asked"
    >
  > = {},
): Proposal => {
  const base = forcedApprover(tx, changes) ?? planApprover(tx, tx.actor, changes, input.to)
  const ownerId = base.ownerId
  // Wave B2: why a person decides. From submitChangeset, the verdicts' reasons; from OpenProposal{plan_change}
  // (propose_plan), the forced reasons of the changes if any, else the agent chose to ask.
  const asked =
    extra.asked ??
    (() => {
      const forced = changes.flatMap((c) => {
        const f = forcedApproverOf(tx.state, tx.actor, c)
        return f ? [f.asked] : []
      })
      return collapseAsked(forced.length > 0 ? forced : [{ rule: "asked" }])
    })()
  const tainted = actorTaint(tx.state, tx.actor) === "tainted"
  // decision 0102: an agent's card goes to the agent above it first (area lead, else the project lead)
  const approver = chainApprover(tx.state, tx.actor, base.approver, {
    body: { _tag: "plan_change", changes } as unknown as Proposal["body"],
    blocks: extra.blocks ?? [],
    ...(asked.length > 0 ? { asked } : {}),
  })
  const intentKey = extra.intentKey ?? `plan_change:${input.changesetId}`
  const dup = tx.state.proposals.find(
    (p) => p.intentKey === intentKey && (p.state === "open" || p.state === "revision_requested"),
  )
  if (dup)
    reject({
      _tag: "Duplicate",
      existing: { _tag: "proposal", id: dup.id },
      reason: `${dup.ref} already proposes this`,
    })
  // decision 0093: one plan card per subject (the same kinds of change to the same items, for the same person). The
  // same agent's newer card replaces its open one; another agent's open card makes this one a duplicate
  const same = sameSubjectCard(
    tx.state,
    tx.actor,
    cardPersonOf({ approver }),
    { kind: "plan_change", key: planSubjectKey(changes) },
    tx.now,
  )
  if (same._tag === "duplicate")
    reject({
      _tag: "Duplicate",
      existing: { _tag: "proposal", id: same.proposal.id },
      reason: `${same.proposal.ref} already proposes this change to the same item(s): leave it to its proposer, or say in your reply what you would change`,
    })
  if (same._tag === "supersede") tx.emit({ _tag: "ProposalWithdrawn", proposalId: same.proposal.id })
  return openProposal(tx, {
    id: proposalId,
    ownerId,
    ...(asked.length > 0 ? { asked } : {}),
    body: {
      _tag: "plan_change",
      changesetId: input.changesetId,
      rationale: input.rationale,
      changes: changes as unknown as readonly [GraphCommand, ...GraphCommand[]],
      baseSeq: tx.base.seq,
      revertOf: input.revertOf,
      undoOf: input.undoOf,
      ...(input.questions && input.questions.length > 0 ? { questions: [...input.questions] } : {}),
      ...(input.questions && input.questions.length > 0 && input.containerId
        ? { containerId: input.containerId }
        : {}),
    },
    proposedBy: tx.actor,
    reasoning: extra.reasoning ?? input.rationale,
    oneLine: extra.oneLine ?? oneLine(input.rationale),
    risk: extra.risk ?? "medium",
    reversibility: extra.reversibility ?? "reversible",
    tier: "soon",
    blocks: extra.blocks ?? [],
    tainted,
    approver,
    decideBy: extra.decideBy ?? null,
    expiresAt: null, // rev 2.1 (decision 0018): proposals do not expire
    intentKey,
  })
}

/** Fold `applied` (in order) over the current state; drop changes that are no longer allowed, repeat to a fixpoint. */
const settleApplied = (
  tx: Tx,
  changes: ReadonlyArray<GraphCommand>,
  applied: Set<number>,
  deps: ReadonlyArray<ReadonlyArray<number>>,
): ReadonlyMap<number, PolicyVerdict> => {
  for (;;) {
    const scratch = new Tx(tx.state, tx.ctx)
    scratch.scope = { ...tx.scope }
    const out = new Map<number, PolicyVerdict>()
    let drop: number | null = null
    for (const i of [...applied].sort((a, b) => a - b)) {
      const change = must(changes[i], "change")
      const v = verdictFor(scratch, change)
      if (v._tag !== "allow" && v._tag !== "exempt") {
        drop = i
        break
      }
      try {
        scratch.withScope({ policy: v.stamp }, () => applyGraph(scratch, change))
      } catch (e) {
        if (!(e instanceof Rejected)) throw e
        drop = i
        break
      }
      out.set(i, v)
    }
    if (drop === null) return out
    applied.delete(drop)
    for (let changed = true; changed; ) {
      changed = false
      for (const j of [...applied])
        if ((deps[j] ?? []).some((d) => !applied.has(d))) {
          applied.delete(j)
          changed = true
        }
    }
  }
}

/**
 * The SubmitChangeset handler. Everything happens in `tx`: on a fully allowed set the scratch fold IS the result;
 * with propose verdicts the fold is redone over the original state for the applied subset.
 */
export const submitChangeset = (tx: Tx, input: ChangesetInput): void => {
  authorizeGraph(tx.actor, input.changes)
  if (input.changes.length === 0) invalid("a changeset needs at least one change", "changes")
  // 1. fold everything over a scratch transaction: authorize, policy, validate
  const scratch = new Tx(tx.state, tx.ctx)
  scratch.scope = { ...tx.scope }
  const verdicts: PolicyVerdict[] = []
  input.changes.forEach((change, index) => {
    const v = verdictFor(scratch, change)
    if (v._tag === "never")
      reject({
        _tag: "Forbidden",
        reason: "policy_never",
        detail: `change ${index} (${change._tag}): ${v.rule}`,
      })
    verdicts.push(v)
    try {
      applyGraph(scratch, change)
    } catch (e) {
      if (e instanceof Rejected && e.rejection._tag === "Invalid")
        reject({ _tag: "Invalid", issues: e.rejection.issues.map((i) => ({ ...i, changeIndex: index })) })
      throw e
    }
  })
  const stampOf = (v: PolicyVerdict): PolicyStamp => (v._tag === "never" ? exemptStamp(null) : v.stamp)
  const allAllowed = verdicts.every((v) => v._tag === "allow" || v._tag === "exempt")
  if (allAllowed) {
    applyChanges(
      tx,
      input,
      input.changes.map((change, index) => ({
        change,
        index,
        stamp: stampOf(must(verdicts[index], "verdict")),
      })),
      null,
    )
    return
  }
  // 2. some changes need approval
  const deps = dependencies(input.changes)
  const applied = new Set<number>()
  if (input.onPropose === "apply_allowed")
    input.changes.forEach((_, i) => {
      const v = must(verdicts[i], "verdict")
      if ((v._tag === "allow" || v._tag === "exempt") && (deps[i] ?? []).every((d) => applied.has(d)))
        applied.add(i)
    })
  // A verdict was judged on a fold that included the proposed changes (e.g. AssignItem-to-me before a CloseItem
  // under own_item). Re-judge the applied subset on its own fold; anything no longer allowed or valid moves into
  // the proposal, with everything that depends on it, until the subset is stable.
  const appliedVerdicts = settleApplied(tx, input.changes, applied, deps)
  const proposed = input.changes.map((c, i) => ({ c, i })).filter(({ i }) => !applied.has(i))
  const proposalChangesetId =
    applied.size > 0
      ? deriveId<ChangesetId>("chg", tx.now, `proposed:${input.changesetId}`)
      : input.changesetId
  const proposalId = deriveId<Ids["ProposalId"]>("prop", tx.now, `changeset:${input.changesetId}`)
  if (applied.size > 0)
    applyChanges(
      tx,
      input,
      [...applied]
        .sort((a, b) => a - b)
        .map((index) => ({
          change: must(input.changes[index], "change"),
          index,
          stamp: stampOf(must(appliedVerdicts.get(index), "verdict")),
        })),
      null,
    )
  // Wave B2 (decision 0057): `reasoning` is the rationale alone; why a person decides is `asked`.
  const asked = collapseAsked(
    proposed
      .map(({ i }) => must(verdicts[i], "verdict"))
      .map(
        (v): AskedReason =>
          v._tag === "propose" ? v.asked : { rule: input.onPropose === "propose_all" ? "asked" : "depends" },
      ),
  )
  proposePlanChange(
    tx,
    {
      changesetId: proposalChangesetId,
      rationale: input.rationale,
      revertOf: input.revertOf ?? null,
      undoOf: input.undoOf ?? null,
      questions: input.questions,
      containerId: input.containerId,
    },
    proposed.map(({ c }) => c),
    proposalId,
    { reasoning: input.rationale, asked },
  )
}

/**
 * Wave A §5: a conflict in plain words, with refs from the state. An item whose status changed since the changeset
 * ("DIC-170 has started since: cancel it or move it instead"); anything else ("DIC-170 was changed since: change it
 * back with a new plan").
 */
const undoConflictText = (
  s: ProjectState,
  events: ReadonlyArray<ProjectEvent>,
  conflict: ReadonlyArray<EntityRef>,
): string => {
  const statusAfter = new Map<string, string>()
  for (const e of events) {
    if (e._tag === "ItemStatusChanged") statusAfter.set(e.itemId, e.to)
    if (e._tag === "ItemCreated") statusAfter.set(e.item.id, e.item.status)
  }
  return conflict
    .map((r) => {
      if (r._tag !== "item") return `${r._tag} ${r.id} was changed since: change it back with a new plan`
      const t = itemOf(s, r.id as Ids["ItemId"])
      const ref = t?.ref ?? r.id
      const was = statusAfter.get(r.id)
      if (t && was !== undefined && t.status !== was) {
        if (t.status === "in_progress" || t.status === "in_review")
          return `${ref} has started since: cancel it or move it instead`
        if (t.status === "done" || t.status === "cancelled")
          return `${ref} is ${t.status === "done" ? "done" : "cancelled"} since: change it with a new plan instead`
        return `${ref} has moved since: move it back with a new plan instead`
      }
      return `${ref} was changed since: change it back with a new plan`
    })
    .join("; ")
}

/** Undo's refusal when the change was undone before (the web shows it as "Undone", not as an error). */
export const ALREADY_UNDONE = "this change was already undone"

/** UndoChangeset: forward-only inverse of one changeset (needs history from the DO). */
export const undoChangeset = (tx: Tx, cmd: { changesetId: ChangesetId; target: ChangesetId }): void => {
  const events = tx.ctx.history?.changesetEvents(cmd.target)
  if (!events)
    invalid("the changeset's events are not available here (undo runs in the Project DO)", "target")
  if (tx.ctx.history?.undoneBy?.(cmd.target) !== undefined) invalid(ALREADY_UNDONE, "target")
  const inv = inverseOf(tx.state, events)
  if ("legacy" in inv)
    invalid(
      "this changeset was made before the work-model update: revert to a plan version instead",
      "target",
    )
  if ("conflict" in inv) invalid(undoConflictText(tx.state, events, inv.conflict), "target")
  if (inv.changes.length === 0) invalid("nothing to undo", "target")
  submitChangeset(tx, {
    changesetId: cmd.changesetId,
    rationale: `Undo ${cmd.target}`,
    changes: inv.changes,
    onPropose: "apply_allowed",
    undoOf: cmd.target,
  })
}

/** RevertPlan: diff the current plan against version v and submit the difference as a new changeset. */
export const revertPlan = (
  tx: Tx,
  cmd: { changesetId: ChangesetId; toVersion: number; rationale: string },
): void => {
  if (cmd.toVersion < 0 || cmd.toVersion >= tx.state.planVersion)
    invalid(`version ${cmd.toVersion} is not an earlier plan version`, "toVersion")
  const target = tx.ctx.history?.stateAtVersion(cmd.toVersion)
  if (!target) invalid("plan history is not available here (revert runs in the Project DO)", "toVersion")
  const changes = revertTo(tx.state, target)
  if (changes.length === 0) invalid("the plan already matches that version", "toVersion")
  submitChangeset(tx, {
    changesetId: cmd.changesetId,
    rationale: cmd.rationale,
    changes,
    onPropose: "apply_allowed",
    revertOf: cmd.toVersion,
  })
}
